import React, { useState, useRef, useEffect, useMemo, useCallback, useImperativeHandle } from 'react';
import type { Ref } from 'react';
import { Box, Button, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { templateService, type OperationMode, NotificationTemplate } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { useSettingsStore, useAuthStore } from '@core/store';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import ReactQuill, { Quill } from 'react-quill-new';
import { TemplateVariable, ActiveLicenses } from '@core/types';
import { validateName, required, requiredArray } from '@core/utils/validation';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { WcnButton } from '@/components';
import EmailInput from '@/components/shared/EmailInput.tsx';
import { equalsIgnoreCase } from '@core/utils';
import EmailInputForShop from '@/components/shared/EmailInputForShop.tsx';
import { UserApiResponse } from '@/services/userService.ts';

const SizeStyle = Quill.import('attributors/style/size') as any;
const FONT_SIZES = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];
SizeStyle.whitelist = FONT_SIZES;
SizeStyle.add = (node: HTMLElement, value: string): boolean => {
    node.style.fontSize = `${value}px`;
    return true;
};
SizeStyle.value = (node: HTMLElement): string => {
    const val = node.style.fontSize;
    return val ? val.replace('px', '') : '';
};
Quill.register(SizeStyle, true);

export interface NotificationTemplatesEditorRef {
    isDirty: boolean;
    /** Performs the save without calling onSave. Returns true if save succeeded. */
    save: () => Promise<boolean>;
}

interface NotificationTemplatesEditorProps {
    template: NotificationTemplate;
    /** Original name to display as a hint below the Name field when the template is a copy. */
    originalName?: string;
    onSave: () => void;
    onCancel: () => void;
    onCopy: (template: NotificationTemplate) => void;
    onDelete: (template: NotificationTemplate) => void;
    ref?: Ref<NotificationTemplatesEditorRef>;
    users: UserApiResponse[];
    operationModes: OperationMode[];
    incidentTypeOptions: { value: string; label: string }[];
}

const TEMPLATE_VARIABLES: TemplateVariable[] = [
    { name: 'shopName', label: 'shopName' },
    { name: 'shopId', label: 'shopId' },
    { name: 'incidentName', label: 'incidentName' },
    { name: 'timestamp', label: 'timestamp' },
    { name: 'device', label: 'device' }
];

const TOOLTIP_SLOT_PROPS = {
    popper: {
        modifiers: [
            { name: 'offset', options: { offset: [0, 16] } },
            { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', altAxis: true } },
            { name: 'flip', enabled: true, options: { fallbackPlacements: ['right', 'top', 'bottom'] } }
        ]
    }
};

export const FieldTooltip = ({ title, maxWidth }: { title: string; maxWidth?: number }) => (
    <Tooltip
        title={title}
        placement="left"
        arrow
        classes={{ tooltip: 'page-title-tooltip' }}
        slotProps={{
            popper: TOOLTIP_SLOT_PROPS.popper,
            ...(maxWidth ? { tooltip: { sx: { maxWidth: `${maxWidth}px !important` } } } : {})
        }}
    >
        <IconButton size="small" sx={{ '& i': { fontSize: '16px !important', color: 'var(--gray-medium)' } }}>
            <i className="bi bi-question-circle" />
        </IconButton>
    </Tooltip>
);

export const NotificationTemplatesEditor: React.FC<NotificationTemplatesEditorProps> = ({
    template: initialTemplate,
    originalName,
    onSave,
    onCancel,
    onCopy,
    onDelete,
    ref,
    users,
    operationModes,
    incidentTypeOptions
}) => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess, notifyWarning } = useNotifications();
    const { isMobile } = useSettingsStore();
    const { hasLicense } = useAuthStore();

    const quillRef = useRef<any>(null);
    const formRef = useRef<FormContainerRef>(null);
    const lastFocusedField = useRef<'subject' | 'body' | null>(null);
    const subjectInputRef = useRef<HTMLInputElement | null>(null);

    const [template, setTemplate] = useState<NotificationTemplate>(initialTemplate);
    const [loading, setLoading] = useState(false);
    const [formValid, setFormValid] = useState(true);
    const [emailInputKey, setEmailInputKey] = useState(0);

    const normalizeHtml = useCallback((html: string): string => {
        const div = document.createElement('div');
        div.innerHTML = html;
        div.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
            const sorted = Array.from(el.style)
                .sort()
                .map((prop) => `${prop}: ${el.style.getPropertyValue(prop)}`)
                .join('; ');
            el.setAttribute('style', sorted);
        });
        return div.innerHTML;
    }, []);

    const normalizedInitialBody = useMemo(() => normalizeHtml(initialTemplate.body), [initialTemplate.body]);
    const normalizedCurrentBody = useMemo(() => normalizeHtml(template.body), [template.body]);

    function isSameStringArray(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        return JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
    }

    const isDirty = useMemo(() => {
        const recipientWithShopsDirty =
            (template.recipientWithShops?.length ?? 0) !== (initialTemplate.recipientWithShops?.length ?? 0) ||
            (template.recipientWithShops ?? []).some((r) => {
                const init = initialTemplate.recipientWithShops?.find((x) => x.eMail === r.eMail);
                return !init || !isSameStringArray(r.shopIds, init.shopIds);
            });

        return (
            template.name !== initialTemplate.name ||
            template.subject !== initialTemplate.subject ||
            normalizedCurrentBody !== normalizedInitialBody ||
                        !isSameStringArray(
                template.isActiveOnStoreOperationModes ?? [],
                initialTemplate.isActiveOnStoreOperationModes ?? []
            ) ||
            !isSameStringArray(template.recipientsForAllShops ?? [], initialTemplate.recipientsForAllShops ?? []) ||
            recipientWithShopsDirty
        );
    }, [template, initialTemplate, normalizedCurrentBody, normalizedInitialBody]);

    const quillModules = useMemo(
        () => ({
            toolbar: [
                [{ font: [] }],
                [
                    {
                        size: [false, ...FONT_SIZES]
                    }
                ],
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ['clean']
            ]
        }),
        []
    );

    useEffect(() => {
        if (!quillRef.current) return;
        const quill = quillRef.current.getEditor();
        const toolbar = quill.getModule('toolbar').container as HTMLElement;

        const formats = Array.from(toolbar.querySelectorAll('.ql-formats')) as HTMLElement[];

        if (isMobile) {
            const ROW_BREAKS = [2, 6];

            const rows = [
                formats.slice(0, ROW_BREAKS[0]),
                formats.slice(ROW_BREAKS[0], ROW_BREAKS[1]),
                formats.slice(ROW_BREAKS[1])
            ].map((group) => {
                const row = document.createElement('div');
                row.className = 'ql-toolbar-row';
                group.forEach((f) => row.appendChild(f));
                return row;
            });

            toolbar.innerHTML = '';
            rows.forEach((row) => toolbar.appendChild(row));
        } else {
            toolbar.innerHTML = '';
            formats.forEach((f) => toolbar.appendChild(f));
        }
    }, [isMobile]);

    useEffect(() => {
        setTemplate(initialTemplate);
        setFormValid(true);
        formRef.current?.resetValidation();
        setEmailInputKey((k) => k + 1);
    }, [initialTemplate]);

    const handleFieldChange = useCallback((field: keyof NotificationTemplate, value: any) => {
        setTemplate((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleInsertVariable = useCallback(
        (variableName: string) => {
            const text = `{${variableName}}`;

            if (lastFocusedField.current === 'subject' && subjectInputRef.current) {
                const input = subjectInputRef.current;
                const start = input.selectionStart ?? input.value.length;
                const end = input.selectionEnd ?? start;
                const current = template.subject ?? '';
                const newValue = current.slice(0, start) + text + current.slice(end);
                handleFieldChange('subject', newValue);
                requestAnimationFrame(() => {
                    input.selectionStart = start + text.length;
                    input.selectionEnd = start + text.length;
                    input.focus();
                });
                return;
            }

            if (quillRef.current) {
                const editor = quillRef.current.getEditor();
                const selection = editor.getSelection();
                const index = selection ? selection.index : editor.getLength();
                editor.insertText(index, text);
                editor.setSelection(index + text.length);
            }
        },
        [template.subject, handleFieldChange]
    );

    const performSave = useCallback(async (): Promise<boolean> => {
        if (loading) return false;
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }
        setLoading(true);
        try {
            const isNew = !template.id;
            const payload = isNew ? (({ id, ...rest }) => rest)(template) : template;
            await templateService.saveNotificationTemplate(payload);
            notifySuccess();
            return true;
        } catch (error) {
            notifyErrorResponse(error);
            return false;
        } finally {
            setLoading(false);
        }
    }, [loading, formValid, template, t]);

    useImperativeHandle(ref, () => ({ isDirty, save: performSave }), [isDirty, performSave]);

    const handleSave = useCallback(async () => {
        const saved = await performSave();
        if (saved) onSave();
    }, [performSave]);

    const userEmails = useMemo(() => users.map((u) => u.eMail), [users]);

    const recipientsForAllShops = useMemo(() => template.recipientsForAllShops ?? [], [template.recipientsForAllShops]);
    const recipientWithShops = useMemo(() => template.recipientWithShops ?? [], [template.recipientWithShops]);

    const disabledForAllShops = useMemo(() => recipientWithShops.map((r) => r.eMail), [recipientWithShops]);
    const disabledForSpecificShops = useMemo(() => recipientsForAllShops, [recipientsForAllShops]);

    const isUnknowEmail = useCallback(
        (email: string): string => {
            if (!userEmails.some((e) => equalsIgnoreCase(e, email))) return 'validation.noAccount';
            return '';
        },
        [userEmails]
    );

    const onErrorForAllShops = useCallback(
        (email: string): string => {
            if (!userEmails.some((e) => equalsIgnoreCase(e, email))) return 'validation.noAccountRecipientNotAllowed';

            if (disabledForAllShops.some((e) => equalsIgnoreCase(e, email)))
                return 'validation.recipientAlreadySelectedInSpecificShops';

            return '';
        },
        [userEmails, disabledForAllShops]
    );

    const onErrorForSpecificShops = useCallback(
        (email: string): string => {
            if (disabledForSpecificShops.some((e) => equalsIgnoreCase(e, email)))
                return 'validation.recipientAlreadySelectedInAllShops';

            return '';
        },
        [disabledForSpecificShops]
    );

    return (
        <Paper variant="outlined" sx={{ borderRadius: '3px', overflow: 'visible' }}>
            {/* Section header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                    {template.id ? t('editNotification') : t('addNotification')}
                </Typography>
            </Box>

            <Box sx={{ padding: isMobile ? '24px 10px' : '42px 100px 24px 180px' }}>
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Grid container spacing={4}>
                        <Grid item container xs={12}>
                            <Grid item xs={10} md={6}>
                                <FormTextField
                                    name="name"
                                    label={t('name')}
                                    value={template.name}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                    rules={[validateName(t)]}
                                    sx={{ maxWidth: '368px' }}
                                    required
                                />
                                {originalName && (
                                    <Typography
                                        variant="caption"
                                        sx={{ pl: 1, color: 'var(--gray-medium)', display: 'block', mt: 0.5 }}
                                    >
                                        ({t('originalName')}: {originalName})
                                    </Typography>
                                )}
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                md={6}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    marginTop: isMobile ? '-8px' : 0
                                }}
                            >
                                <FieldTooltip title={t('notificationTemplateNameTooltip')} maxWidth={200} />
                            </Grid>
                        </Grid>

                        <Grid item container xs={12}>
                            <Grid item xs={10} md={6}>
                                <FormSelect
                                    name="discriminator"
                                    label={t('incidentType')}
                                    items={incidentTypeOptions}
                                    value={template.incidentType || ''}
                                    itemText="label"
                                    itemValue="value"
                                    placeholder={t('selectAType')}
                                    onChange={(value) => handleFieldChange('incidentType', value)}
                                    rules={[required(t)]}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                md={6}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    marginTop: isMobile ? '-8px' : 0
                                }}
                            >
                                <FieldTooltip title={t('notificationTemplateTypeTooltip')} />
                            </Grid>
                        </Grid>

                        {hasLicense(ActiveLicenses.ShopAutomation) && (
                            <Grid item container xs={12}>
                                <Grid item xs={10} md={6}>
                                    <FormSelect
                                        selectAll
                                        name="operationModesForTemplate"
                                        items={operationModes}
                                        itemText={'name'}
                                        itemValue={'name'}
                                        label={t('notificationTemplateOperationModeSelect')}
                                        value={template.isActiveOnStoreOperationModes || []}
                                        onChange={(value) => handleFieldChange('isActiveOnStoreOperationModes', value)}
                                        rules={[requiredArray(t)]}
                                        multiple
                                        selectDisplayLabels={{
                                            allLabel: t('allOperationModes'),
                                            placeholderLabel: t('selectOperationModes'),
                                            countLabel: t('selectedWithValue')
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={2}
                                    md={6}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: isMobile ? 'flex-start' : 'center',
                                        marginTop: isMobile ? '-8px' : 0
                                    }}
                                >
                                    <FieldTooltip title={t('descriptions.templateOperationMode')} maxWidth={300} />
                                </Grid>
                            </Grid>
                        )}

                        <Grid item container xs={12} gap={1}>
                            <Grid item container xs={12} alignItems="center" justifyContent="space-between">
                                <Grid item xs={10}>
                                    <Typography
                                        variant="body2"
                                        sx={{ pl: 1, color: 'var(--gray-medium)', fontSize: '12px' }}
                                    >
                                        {t('insertVariables')}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={2}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: isMobile ? 'flex-start' : 'center',
                                        marginTop: isMobile ? '-8px' : 0
                                    }}
                                >
                                    <FieldTooltip
                                        title={t('notificationTemplateInsertVariableTooltip')}
                                        maxWidth={250}
                                    />
                                </Grid>
                            </Grid>
                            {isMobile ? (
                                <Grid item container xs={12}>
                                    {TEMPLATE_VARIABLES.map((variable) => (
                                        <Grid key={variable.name} item lg={2} xs={6}>
                                            <Button
                                                variant="text"
                                                startIcon={<i className="bi bi-code-square" />}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleInsertVariable(variable.name)}
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    borderRadius: 0,
                                                    color: 'black',
                                                    textAlign: 'justify',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                {t(variable.label)}
                                            </Button>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 2.5 }}>
                                    {TEMPLATE_VARIABLES.map((variable) => (
                                        <Button
                                            key={variable.name}
                                            variant="text"
                                            startIcon={<i className="bi bi-code-square" />}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleInsertVariable(variable.name)}
                                            sx={{
                                                textTransform: 'uppercase',
                                                borderRadius: 0,
                                                color: 'black',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {t(variable.label)}
                                        </Button>
                                    ))}
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Grid item xs={10}>
                                <FormTextField
                                    name="subject"
                                    fullWidth
                                    label={t('subjectLine')}
                                    value={template.subject}
                                    onChange={(e) => handleFieldChange('subject', e.target.value)}
                                    onFocus={() => {
                                        lastFocusedField.current = 'subject';
                                    }}
                                    inputRef={subjectInputRef}
                                    rules={[required(t)]}
                                    required
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12} className="quill-wrapper">
                            <Grid item xs={11}>
                                <Typography
                                    variant="body2"
                                    sx={{ pl: 1, mb: 1, color: 'var(--gray-medium)', fontSize: '12px' }}
                                >
                                    {t('templateContent')}
                                </Typography>
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={template.body}
                                    modules={quillModules}
                                    onChange={(value) => handleFieldChange('body', value)}
                                    onFocus={() => {
                                        lastFocusedField.current = 'body';
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item container xs={12}>
                            <Grid item xs={10}>
                                <Box sx={{ maxWidth: '368px' }}>
                                    <EmailInput
                                        key={emailInputKey}
                                        recipientEmails={userEmails}
                                        initialEmails={recipientsForAllShops}
                                        onUpdate={(emails) => handleFieldChange('recipientsForAllShops', emails)}
                                        label={t('recipientsForAllShops')}
                                        disabledEmails={disabledForAllShops}
                                        onError={onErrorForAllShops}
                                    />
                                </Box>
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    marginTop: isMobile ? '-8px' : 0
                                }}
                            >
                                <FieldTooltip title={t('descriptions.recipientsForAllShops')} maxWidth={300} />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <EmailInputForShop
                                key={`shop-${emailInputKey}`}
                                users={users}
                                initialEmails={recipientWithShops}
                                onUpdate={(recipients) => handleFieldChange('recipientWithShops', recipients)}
                                disabledEmails={disabledForSpecificShops}
                                onChipWarning={isUnknowEmail}
                                onError={onErrorForSpecificShops}
                            />
                        </Grid>
                    </Grid>
                </FormContainer>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        alignItems: isMobile ? 'center' : 'unset',
                        gap: 2,
                        mt: 5
                    }}
                >
                    {isMobile ? (
                        <>
                            <WcnButton
                                variant="primary"
                                onClick={handleSave}
                                disabled={loading || !formValid}
                                style={{ width: '270px' }}
                            >
                                {t('save')}
                            </WcnButton>
                            {template.id && (
                                <WcnButton
                                    variant="primary"
                                    onClick={() => onCopy(template)}
                                    disabled={loading || !formValid}
                                    style={{ width: '270px' }}
                                >
                                    {t('copyTemplate')}
                                </WcnButton>
                            )}
                            <WcnButton variant="red" onClick={onCancel} style={{ width: '270px' }}>
                                {t('cancel')}
                            </WcnButton>
                            {template.id && (
                                <WcnButton
                                    variant="red"
                                    disabled={template.isDefault}
                                    onClick={() => onDelete(template)}
                                    style={{ width: '270px' }}
                                >
                                    {t('deleteNotification')}
                                </WcnButton>
                            )}
                        </>
                    ) : (
                        <>
                            {template.id && (
                                <WcnButton
                                    variant="red"
                                    disabled={template.isDefault}
                                    onClick={() => onDelete(template)}
                                    style={{ width: '240px' }}
                                >
                                    {t('deleteNotification')}
                                </WcnButton>
                            )}
                            <WcnButton variant="red" onClick={onCancel}>
                                {t('cancel')}
                            </WcnButton>
                            {template.id && (
                                <WcnButton
                                    variant="primary"
                                    onClick={() => onCopy(template)}
                                    disabled={loading || !formValid}
                                >
                                    {t('copyTemplate')}
                                </WcnButton>
                            )}
                            <WcnButton variant="primary" onClick={handleSave} disabled={loading || !formValid}>
                                {t('save')}
                            </WcnButton>
                        </>
                    )}
                </Box>

                <LoadingOverlay open={loading} />
            </Box>
        </Paper>
    );
};

export default NotificationTemplatesEditor;
